import { useEffect, useState } from 'react';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Text, Button, Collapsible } from '@terra/ui-design-system';
import {
  Alert01Icon,
  ArrowDown01Icon,
  ArrowRight01Icon,
  LinkSquare02Icon,
  InformationCircleIcon,
  Shield01Icon,
} from '@terra/ui-icons';
import { useElectronAPI } from '../hooks/useElectronAPI';
import { useProxyStore } from '../store/proxy-store';
import { useProxyActions } from '../hooks/useProxyActions';

type OS = 'darwin' | 'win32' | 'linux' | 'unknown';

export function CertificateInstallSection() {
  const { isCACertificateInstalled, getAppInfo } = useElectronAPI();
  const { isProxyRunning, settings } = useProxyStore();
  const {
    installCACertificate,
    startProxy,
    isLoading: isActionLoading,
  } = useProxyActions();
  const proxyUrl = `http://${settings.host}:${settings.port}`;
  const [isInstalling, setIsInstalling] = useState(false);
  const [certStatus, setCertStatus] = useState<{
    isInstalled: boolean;
    isChecking: boolean;
  }>({
    isInstalled: false,
    isChecking: true,
  });
  const [showInstructions, setShowInstructions] = useState(false);
  const [os, setOs] = useState<OS>('unknown');

  useEffect(() => {
    const getOS = () => {
      try {
        const appInfo = getAppInfo();

        setOs(appInfo.platform as OS);
      } catch (error) {
        console.error('Failed to get platform:', error);
      }
    };

    getOS();
  }, [getAppInfo]);
  useEffect(() => {
    const checkCertificate = async () => {
      setCertStatus((prev) => ({ ...prev, isChecking: true }));

      try {
        const result = await isCACertificateInstalled();

        setCertStatus({
          isInstalled: result.success && result.data?.installed === true,
          isChecking: false,
        });
      } catch (error) {
        console.error('Failed to check certificate status:', error);
        setCertStatus({
          isInstalled: false,
          isChecking: false,
        });
      }
    };

    void checkCertificate();
    const handleFocus = () => {
      void checkCertificate();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [isCACertificateInstalled]);
  const { isInstalled, isChecking } = certStatus;
  // Handle install certificate - starts proxy first if not running
  const handleInstallCertificate = async () => {
    setIsInstalling(true);

    try {
      // Start proxy if not running (required to serve the certificate)
      if (!isProxyRunning) {
        await startProxy();
        // Wait a moment for proxy to fully start
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      // Install the certificate
      await installCACertificate();
    } catch (error) {
      console.error('Failed to install certificate:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  // Don't show anything if certificate is installed
  if (isInstalled) {
    return null;
  }

  const iconElement = isChecking ? (
    <InformationCircleIcon size={24} color="var(--colors-fg-muted)" />
  ) : (
    <Alert01Icon size={24} color="var(--colors-orange-9)" />
  );
  const titleText = isChecking
    ? 'Checking Certificate...'
    : 'Certificate Required';
  const descriptionText = isChecking
    ? 'Verifying CA certificate installation status...'
    : 'Install the CA certificate to intercept HTTPS traffic without browser warnings.';
  const getOsInstructions = () => {
    switch (os) {
      case 'darwin':
        return (
          <VStack gap={2} alignItems="flex-start" w="full">
            <Text fontSize="xs" fontWeight="medium">
              macOS Installation:
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              1. Download the certificate from the link above
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              2. Double-click the downloaded .crt file
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              3. Add to &quot;login&quot; or &quot;System&quot; keychain
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              4. Find the certificate in Keychain Access, double-click it
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              5. Expand &quot;Trust&quot; and set to &quot;Always Trust&quot;
            </Text>
          </VStack>
        );
      case 'win32':
        return (
          <VStack gap={2} alignItems="flex-start" w="full">
            <Text fontSize="xs" fontWeight="medium">
              Windows Installation:
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              1. Download the certificate from the link above
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              2. Double-click the downloaded .crt file
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              3. Click &quot;Install Certificate&quot;
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              4. Select &quot;Local Machine&quot; and click Next
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              5. Select &quot;Place all certificates in the following
              store&quot;
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              6. Browse and select &quot;Trusted Root Certification
              Authorities&quot;
            </Text>
          </VStack>
        );
      case 'linux':
        return (
          <VStack gap={2} alignItems="flex-start" w="full">
            <Text fontSize="xs" fontWeight="medium">
              Linux Installation:
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              1. Download the certificate from the link above
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              2. Copy to CA certificates directory:
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={4} fontFamily="mono">
              sudo cp terra-proxy-ca.crt /usr/local/share/ca-certificates/
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={2}>
              3. Update CA certificates:
            </Text>
            <Text fontSize="xs" color="fg.muted" pl={4} fontFamily="mono">
              sudo update-ca-certificates
            </Text>
          </VStack>
        );
      default:
        return (
          <Text fontSize="xs" color="fg.muted">
            Download the certificate and install it in your system&apos;s
            trusted certificate store.
          </Text>
        );
    }
  };

  return (
    <Box
      w="full"
      maxW="md"
      p={4}
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="md"
      bg="bg.subtle"
    >
      <VStack gap={4} alignItems="flex-start" w="full">
        <HStack gap={3} alignItems="center" w="full">
          {iconElement}
          <VStack alignItems="flex-start" gap={1} flex={1}>
            <Text fontSize="md" fontWeight="semibold" color="fg.default">
              {titleText}
            </Text>
            <Text fontSize="sm" color="fg.muted">
              {descriptionText}
            </Text>
          </VStack>
        </HStack>

        {!isInstalled && !isChecking && (
          <VStack gap={3} w="full" alignItems="stretch">
            {/* Install Certificate Button */}
            <Button
              size="sm"
              w="full"
              onClick={() => void handleInstallCertificate()}
              loading={isInstalling || isActionLoading}
            >
              <HStack gap={2}>
                <Shield01Icon size={16} />
                {!isProxyRunning
                  ? 'Start Proxy & Install Certificate'
                  : 'Install Certificate'}
              </HStack>
            </Button>

            {/* Alternative: Download from proxy URL */}
            {isProxyRunning && (
              <HStack gap={2} justifyContent="center" w="full">
                <Text fontSize="xs" color="fg.muted">
                  Or download from
                </Text>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => window.open(proxyUrl, '_blank')}
                >
                  <HStack gap={1}>
                    <Text fontSize="xs">{proxyUrl}</Text>
                    <LinkSquare02Icon size={12} />
                  </HStack>
                </Button>
              </HStack>
            )}

            <Collapsible.Root
              open={showInstructions}
              onOpenChange={(details) => setShowInstructions(details.open)}
            >
              <Collapsible.Trigger asChild>
                <Button variant="ghost" size="xs" w="full">
                  <HStack gap={1}>
                    {showInstructions ? (
                      <ArrowDown01Icon size={14} />
                    ) : (
                      <ArrowRight01Icon size={14} />
                    )}
                    <Text fontSize="xs">Installation Instructions</Text>
                  </HStack>
                </Button>
              </Collapsible.Trigger>
              <Collapsible.Content>
                <Box
                  mt={2}
                  p={3}
                  borderRadius="md"
                  bg="bg.muted"
                  borderWidth="1px"
                  borderColor="border.subtle"
                >
                  {getOsInstructions()}
                </Box>
              </Collapsible.Content>
            </Collapsible.Root>
          </VStack>
        )}
      </VStack>
    </Box>
  );
}
